// ABOUTME: Notice shown above a Data Table when other users changed rows of the current page:
// ABOUTME: lists the affected rows and fields and offers to refresh the page or dismiss it.

import { Alert, Box, Button, Stack } from '@ds/coolshop';
import { useIntl } from 'react-intl';

import type { RemoteNoticeEntry } from './hooks/useRemoteRowUpdates';
import { getColumnLabel, RemoteUpdatesSchema } from './remoteUpdatesLabels';

type RemoteUpdatesNoticeProps = {
    notice: RemoteNoticeEntry[];
    schema?: RemoteUpdatesSchema;
    defaultLanguage: string;
    onRefresh: () => void;
    onDismiss: () => void;
};

export function RemoteUpdatesNotice({
    notice,
    schema,
    defaultLanguage,
    onRefresh,
    onDismiss,
}: RemoteUpdatesNoticeProps) {
    const intl = useIntl();

    if (notice.length === 0) return null;

    const describe = (entry: RemoteNoticeEntry) => {
        if (entry.kind === 'deleted') {
            return intl.formatMessage({
                id: 'data-table.remote-updates.deleted',
                defaultMessage: 'deleted',
            });
        }
        const fields = entry.fieldCodes
            .map(columnId => getColumnLabel(schema, columnId, defaultLanguage))
            .join(', ');
        return entry.kind === 'conflict'
            ? intl.formatMessage(
                  {
                      id: 'data-table.remote-updates.conflict',
                      defaultMessage: '{fields} (conflicts with your edit)',
                  },
                  { fields },
              )
            : fields;
    };

    return (
        <Box mb={1}>
            <Alert
                severity="info"
                action={
                    <Stack direction="row" gap={1}>
                        <Button size="small" onClick={onRefresh}>
                            {intl.formatMessage({
                                id: 'data-table.remote-updates.refresh',
                                defaultMessage: 'Refresh',
                            })}
                        </Button>
                        <Button size="small" onClick={onDismiss}>
                            {intl.formatMessage({
                                id: 'data-table.remote-updates.dismiss',
                                defaultMessage: 'Dismiss',
                            })}
                        </Button>
                    </Stack>
                }
            >
                {intl.formatMessage(
                    {
                        id: 'data-table.remote-updates.title',
                        defaultMessage:
                            '{count, plural, one {# row was changed by another user} other {# rows were changed by other users}}',
                    },
                    { count: notice.length },
                )}
                <Box component="ul" m={0} pl={2}>
                    {notice.map(entry => (
                        <li key={entry.nodeId}>
                            <strong>{entry.code}</strong>: {describe(entry)}
                        </li>
                    ))}
                </Box>
            </Alert>
        </Box>
    );
}
